import { useContext, useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { chain, partition, noop } from "lodash"
import { OpenInNew } from "@mui/icons-material"
import SearchableMenu from "components/searchable-menu"
import { useIsOpPro, useAccountAlias } from "resources/account"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { hasPermission, PERMISSION_LEVELS } from "helpers/permissions"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "../../analytics"
import { ASSET_VIEW_EVENTS } from "connected-components/asset-view/analytics/events"
import { useMetricsSelectorAnalytics } from "components/custom-metrics-selector/hooks/use-metrics-selector-analytics"
import { useModules } from "connected-components/dashboard/context/modules"
import { buildAssetViewConfigForNewTab } from "connected-components/dashboard/helpers/module-renderers"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import {
  GROUP_IDS,
  GROUP_LABELS,
  buildDefaultViewItems,
  buildContentSetViewItems,
  mergeItemsWithOrderAndSecondaryAction,
} from "./assets-menu-items"

import "./style.scss"

function getActions(account, template, setView, deleteTemplate, setShowMetricsSelector, track = noop) {
  const actions = []
  const hasWriteAccess = hasPermission(template.permissionLevel, PERMISSION_LEVELS.WRITE)
  if (hasWriteAccess) {
    actions.push({
      id: "edit",
      label: "Edit",
      onClick: () => {
        track(DASHBOARD_EVENTS.VIEW_OPENED, { templateId: template.id, templateName: template.name })
        setView(template.id)
        setShowMetricsSelector(true)
      },
    })
  }

  const hasDeleteAccess = hasPermission(template.permissionLevel, PERMISSION_LEVELS.OWNER)
  if (hasDeleteAccess) {
    actions.push(...[{
      id: "permissions",
      component: <div className="permissions-menu-item">
        <span>Permissions</span>
        <OpenInNew fontSize="small" />
      </div>,
      onClick: () => {
        track(DASHBOARD_EVENTS.VIEW_PERMISSIONS_CLICKED, { templateId: template.id, templateName: template.name })
        window.open(`/${account}/template/${template.id}/permissions`, "_blank")
      },
    }, {
      id: "delete",
      label: "Delete",
      onClick: async () => {
        await deleteTemplate(template)
        track(DASHBOARD_EVENTS.VIEW_DELETED, { templateId: template.id, templateName: template.name })
        setView("exposure")
      },
    }])
  }

  return actions
}

export default function AssetsViewSelector({ context, templates, contentSets }) {
  const { view, setView, setShowMetricsSelector, deleteTemplate } = useContext(context)
  const { addModule } = useModules()
  // TODO: This should be replaced by the proper Analytics context
  const analytics = useAnalytics()
  const assetViewContext = useContext(context)
  const metricsSelectorAnalytics = useMetricsSelectorAnalytics(assetViewContext)

  const isOpPro = useIsOpPro()
  const account = useAccountAlias()
  const [proprietaryContentSets, licensedContentSets] = useMemo(() => partition(contentSets, "proprietary"), [contentSets])

  const defaultMenuItems = useMemo(() => buildDefaultViewItems(isOpPro), [isOpPro])
  const contentSetMenuItems = useMemo(() => buildContentSetViewItems(licensedContentSets, proprietaryContentSets), [licensedContentSets, proprietaryContentSets])
  const customMenuItems = useMemo(() => [
    {
      id: "custom",
      label: "New View...",
      groupId: GROUP_IDS.CUSTOM,
      groupLabel: GROUP_LABELS.CUSTOM,
      onSelect: () => {
        metricsSelectorAnalytics.trackOpened()
        setShowMetricsSelector(true)
      },
    },
    ...chain(templates)
      .sortBy("label")
      .map((template) => ({
        id: template.id,
        label: template.name,
        groupId: GROUP_IDS.CUSTOM,
        groupLabel: GROUP_LABELS.CUSTOM,
        actions: getActions(account, template, setView, deleteTemplate, setShowMetricsSelector, analytics?.track),
      }))
      .value(),
  ], [setShowMetricsSelector, setView, templates, deleteTemplate, account, metricsSelectorAnalytics, analytics?.track])

  const handleOpenInNewTab = useCallback((item) => {
    analytics.track(ASSET_VIEW_EVENTS.TAB_SELECTED, { selectedTab: item.id })
    const config = buildAssetViewConfigForNewTab({ title: item.label, initialView: item.id })
    addModule({
      type: MODULE_TYPES.ASSET_VIEW,
      config,
    })
    analytics.track(DASHBOARD_EVENTS.MODULE_ADDED, {
      moduleType: MODULE_TYPES.ASSET_VIEW,
      ...config,
    })
  }, [analytics, addModule])

  const menuItems = useMemo(() => mergeItemsWithOrderAndSecondaryAction(
    [customMenuItems, defaultMenuItems, contentSetMenuItems],
    { icon: OpenInNew, onOpenInNewTab: handleOpenInNewTab },
  ), [customMenuItems, defaultMenuItems, contentSetMenuItems, handleOpenInNewTab])

  const handleSelect = useCallback((selectedItem) => {
    if (selectedItem.onSelect) {
      selectedItem.onSelect()
    }
    analytics.track(ASSET_VIEW_EVENTS.TAB_SELECTED, { selectedTab: selectedItem.id })
    setView(selectedItem.id)
  }, [analytics, setView])

  return <SearchableMenu items={menuItems} current={view} onSelect={handleSelect} />
}

AssetsViewSelector.propTypes = {
  context: PropTypes.object.isRequired,
  templates: PropTypes.array.isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
}
